import type { AdminSubscriptionDto } from "@bafe/api-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "../../lib/api.js";
import { presentError } from "../../lib/errors.js";
import { formatDate, formatMoney } from "../../lib/format.js";

function humaniseStatus(value: string): string {
	return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export { PlatformUserManagement as PlatformUsersPage } from "./PlatformUserManagement.js";

export function PlatformBillingPage() {
	const [status, setStatus] = useState("");
	const [workspaceQuery, setWorkspaceQuery] = useState("");
	const subscriptions = useQuery({
		queryKey: ["admin-subscriptions", status],
		queryFn: () => api.adminListSubscriptions({ status: status || undefined }),
	});

	const rows = useMemo(() => {
		const list = subscriptions.data ?? [];
		const needle = workspaceQuery.trim().toLowerCase();
		if (!needle) return list;
		return list.filter((row) => row.organisationName.toLowerCase().includes(needle));
	}, [subscriptions.data, workspaceQuery]);

	return (
		<div>
			<p className="sub">Subscriptions and invoices across every workspace on this server.</p>
			<div className="row head-actions">
				<label className="sr-only" htmlFor="platform-billing-workspace">
					Filter by workspace
				</label>
				<input
					id="platform-billing-workspace"
					className="search"
					type="search"
					placeholder="Filter by workspace name"
					value={workspaceQuery}
					onChange={(event) => setWorkspaceQuery(event.target.value)}
				/>
				<label className="sr-only" htmlFor="platform-billing-status">
					Subscription status
				</label>
				<select
					id="platform-billing-status"
					value={status}
					onChange={(event) => setStatus(event.target.value)}
				>
					<option value="">All statuses</option>
					<option value="active">Active</option>
					<option value="trialing">Trialing</option>
					<option value="past_due">Past due</option>
					<option value="grace_period">Grace period</option>
					<option value="restricted">Restricted</option>
					<option value="suspended">Suspended</option>
					<option value="cancelled">Cancelled</option>
				</select>
			</div>
			{subscriptions.isPending && <p className="empty">Loading billing records.</p>}
			{subscriptions.isError && <p className="err">{presentError(subscriptions.error).title}</p>}
			{subscriptions.data && (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Workspace</th>
								<th>Plan</th>
								<th>Status</th>
								<th>Renews</th>
								<th>Latest invoice</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<PlatformBillingRow key={row.organisationId} row={row} />
							))}
						</tbody>
					</table>
					{rows.length === 0 && <p className="empty">No billing records match this filter.</p>}
				</div>
			)}
		</div>
	);
}

function PlatformBillingRow({ row }: { row: AdminSubscriptionDto }) {
	const invoice = row.latestInvoice;
	return (
		<tr>
			<td>{row.organisationName}</td>
			<td className="quiet">{row.subscription.plan.name}</td>
			<td className="quiet">
				{humaniseStatus(row.subscription.status)}
				{row.paymentIssue ? " · Payment needs attention" : ""}
			</td>
			<td className="quiet">{formatDate(row.subscription.renewsAt)}</td>
			<td className="quiet">
				{invoice
					? `${formatMoney(invoice.amountMinor, invoice.currency)} · ${humaniseStatus(invoice.status)}`
					: "None"}
			</td>
		</tr>
	);
}
